import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useIsFocused, useResponsive } from '@sg/garnish';

import { useGroupOrderIntroState, useGroupOrderingContent } from '@order/features-group-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { LoadingAnimation } from '../../../../components';
import { GroupOrderIntroModal } from '../../../../components/GroupOrderIntroModal';
import { useHomeContent, useHomeLastOrderStatus } from '../../hooks';
import { HomeScreenContentSM } from './HomeScreenContentSM';
import { HomeScreenContentXS } from './HomeScreenContentXS';

export const HomeScreenContent = () => {
  const router = useRouter();
  const { currentBreakpoint } = useResponsive();

  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');
  const isScreenFocused = useIsFocused();

  // ─── Telemetry ───────────────────────────────────────────────────────

  const { track } = useTelemetry();

  // ─── Group Order Intro ───────────────────────────────────────────────

  const handleIntroComplete = useCallback(() => {
    router.navigate('/group-ordering/create');
  }, [router]);

  const handleIntroShow = useCallback(() => {
    track('group_ordering.intro.shown');
  }, [track]);

  const {
    visible: isIntroVisible,
    show: showIntro,
    hide: hideIntro,
  } = useGroupOrderIntroState({
    onShow: handleIntroShow,
    onComplete: handleIntroComplete,
  });

  const { mappedContent: groupOrderingContent, isFetching: isFetchingGroupOrderingContent } =
    useGroupOrderingContent({ pause: !isScreenFocused });

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleCtaPress = useCallback(() => {
    track('home.hero.cta_tapped');
  }, [track]);

  const handleSecondaryCtaPress = useCallback(() => {
    track('group_ordering.entry_point.tapped');
    showIntro();
  }, [showIntro, track]);

  const handleBagPress = useCallback(() => {
    track('home.bag.cta_tapped');
  }, [track]);

  const handlePrimaryCtaPress = useCallback(() => {
    track('group_ordering.intro.primary_cta.tapped');
  }, [track]);

  const handleSecondaryCtaInModalPress = useCallback(() => {
    track('group_ordering.intro.secondary_cta.tapped');
    track('group_ordering.intro.dismissed');

    hideIntro();
  }, [hideIntro, track]);

  const handleStartOrder = useCallback(() => {
    track('group_ordering.intro.primary_cta.tapped');
    track('group_ordering.intro.dismissed');

    hideIntro();
  }, [hideIntro, track]);

  // ─── Data ────────────────────────────────────────────────────────────
  // Used from 'cache-only' in internal components.

  useHomeLastOrderStatus('cache-and-network');

  // ─── Contentful ──────────────────────────────────────────────────────

  const { hasPlacedOrder, homeContent, isFetchingHomeContent } = useHomeContent();

  // ─── Rendering ───────────────────────────────────────────────────────

  if (isFetchingHomeContent || isFetchingGroupOrderingContent) {
    return <LoadingAnimation />;
  }

  if (currentBreakpoint.isXS) {
    return (
      <>
        <HomeScreenContentXS
          homeContent={homeContent}
          bagPressed={handleBagPress}
          ctaPressed={handleCtaPress}
          secondaryCtaLabel={groupOrderingContent.entryPointCtaLabel}
          secondaryCtaPressed={handleSecondaryCtaPress}
          shouldRenderSecondaryCta={isGroupOrderingEnabled}
        />

        <GroupOrderIntroModal
          visible={isIntroVisible}
          content={groupOrderingContent}
          onPrimaryCtaPress={handlePrimaryCtaPress}
          onSecondaryCtaPress={handleSecondaryCtaInModalPress}
          onStartOrder={handleStartOrder}
        />
      </>
    );
  }

  return (
    <>
      <HomeScreenContentSM
        homeContent={homeContent}
        hasPlacedOrder={hasPlacedOrder}
        ctaPressed={handleCtaPress}
        secondaryCtaLabel={groupOrderingContent.entryPointCtaLabel}
        secondaryCtaPressed={handleSecondaryCtaPress}
        shouldRenderSecondaryCta={isGroupOrderingEnabled}
      />

      <GroupOrderIntroModal
        visible={isIntroVisible}
        content={groupOrderingContent}
        onPrimaryCtaPress={handlePrimaryCtaPress}
        onSecondaryCtaPress={handleSecondaryCtaInModalPress}
        onStartOrder={handleStartOrder}
      />
    </>
  );
};
